<template>
  <transition
    name="ui-dropdown"
    @after-enter="allowClose = true"
    @before-leave="allowClose = false"
  >
    <div
      v-if="visible"
      v-click-outside="close"
      :class="classList"
    >
      <div v-if="$slots.header">
        <slot name="header" />
      </div>
      <div
        ref="content"
        :class="[
          'ui-dropdown-content',
        ]"
      >
        <perfect-scrollbar
          ref="scrollview"
          :class="[
            'ui-dropdown-scrollview',
            { 'ui-dropdown-scrollable': contentScrollable },
          ]"
        >
          <slot :close="close" />
        </perfect-scrollbar>
        <div
          v-if="contentScrollable && overlayHeight"
          class="ui-dropdown-overlay"
          :style="{ transform: `translateY(${overlayOffset}px)`, height: `${overlayHeight}px` }"
        />
      </div>
      <div v-if="$slots.footer" class="ui-dropdown-footer">
        <slot name="footer" />
      </div>
    </div>
  </transition>
</template>

<script>
import { throttle } from 'throttle-debounce';

export default {
  name: 'UiDropdown',
  props: {
    text: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    overlayHeight: {
      type: [Number, String],
      default: 0,
    },
    wrapperClass: {
      type: [String, Array, Object],
      default: '',
    },
    left: {
      type: Boolean,
      default: () => false,
    },
    right: {
      type: Boolean,
      default: () => false,
    },
    trackResize: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    contentScrollable: false,
    overlayOffset: 1,
    allowClose: false,
    resizeObserver: null,
    mutationObserver: null,
  }),

  computed: {
    classList() {
      return [
        'ui-dropdown',
        { 'ui-dropdown-left': this.left },
        { 'ui-dropdown-right': this.right },
        ...this.wrapperClass,
      ];
    },
  },

  watch: {
    visible(visible) {
      if (visible) {
        this.$nextTick(() => {
          this.contentScrollable = this.$refs.scrollview.$el.scrollHeight >= 420;

          if (this.contentScrollable) {
            this.initScrollListener();
          }

          if (this.trackResize) {
            this.initResizeObserver();
          }
        });
      }
    },
  },

  beforeUnmount() {
    this.$refs.scrollview?.$el?.removeEventListener?.('scroll', this.onContentScroll);
    if (this.trackResize) {
      this.stopObserve();
    }
  },

  methods: {
    close(e) {
      if (this.allowClose) this.$emit('on-close', e);
    },

    initScrollListener() {
      this.$refs.scrollview?.$el?.addEventListener?.('scroll', this.onContentScroll);
    },

    onContentScroll: throttle(100, function () {
      if (this.$refs.scrollview) {
        const { scrollHeight, scrollTop, offsetHeight } = this.$refs.scrollview.$el;
        const scrollRemaining = scrollHeight - scrollTop - offsetHeight;
        this.overlayOffset = scrollRemaining < this.overlayHeight
          ? this.overlayHeight - scrollRemaining
          : 0;
      }
    }),

    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.$refs?.scrollview) {
          this.contentScrollable = this.$refs.scrollview.$el.scrollHeight >= 420;
        }
      });

      this.resizeObserver.observe(this.$refs.scrollview.$el);
    },

    stopObserve() {
      this.resizeObserver?.disconnect();
    },
  },
};
</script>

<style lang="scss">
.ui-dropdown {
  @include stacking("dropdown");
  position: absolute;
  transition: all .2s;
  top: 0;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(51, 51, 51, .16);

  &-enter,
  &-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
  &-enter-to,
  &-leave {
    opacity: 1;
  }

  &-content {
    position: relative;
    overflow: hidden;
  }
  &-scrollview {
    max-height: 420px;
    overflow-y: auto;
    overflow-x: hidden;
  }
  &-overlay {
    position: absolute;
    pointer-events: none;
    transition: transform .1s;
    bottom: 0;
    width: 100%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, $white 100%);
  }
  &-footer {
    button {
      border-radius: 0;
    }
  }

  // Position
  &-left {
    left: 0;
  }

  // Position
  &-right {
    right: 0;
  }
}
</style>
